import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { useDeleteQaChunk, useGetQaChunks, useImportQaChunk } from 'apis/business/agent/qaChunks/hook';
import { QaChunkResponse } from 'apis/business/agent/qaChunks/types';
import DownloadIcon from 'assets/icons/DownloadIcon';
import ExportIcon from 'assets/icons/ExportIcon';
import ImportIcon from 'assets/icons/ImportIcon';
import Button from 'components/Common/Button';
import DataTable from 'components/Common/DataTable';
import Pagination, { usePagination } from 'components/Common/Pagination';
import Search from 'components/Common/Search';
import { useMemo, useState } from 'react';
import { GoPlus } from 'react-icons/go';
import { MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { Link, useParams } from 'react-router-dom';
import { fileDownloader } from 'utils/helperFunctions';
import http from 'utils/http';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import RetrievalTestingButton from '@/pages/Business/Setting/DataSet/Components/RetrievalTestingButton';
import { useGetCurrentUser } from '@/apis/user/hook';

const SOURCE_LABEL_MAP: Record<string, string> = {
  ai_recommendation: 'AI Recommendations',
  ai_recommendations: 'AI Recommendations',
  ai: 'AI Recommendations',
  website: 'Website',
  custom: 'Custom',
  default: 'Default'
};

export const SOURCE_BADGE_SX: Record<string, object> = {
  ai_recommendations: {
    color: '#246BFD',
    backgroundColor: '#EDF3FF'
  },
  custom: {
    color: '#8338C5',
    backgroundColor: '#F2E8FF'
  },
  website: {
    color: '#EE9518',
    backgroundColor: '#FFF2DF'
  },
  default: {
    color: '#7A7A7A',
    backgroundColor: '#F4F4F4'
  }
};

export const getSourceMeta = (
  source: unknown
): { label: string; key: 'ai_recommendations' | 'custom' | 'default' | 'website' } => {
  const normalized = String(source ?? 'default')
    .trim()
    .toLowerCase()
    .replace(/ /g, '_');

  if (normalized in SOURCE_LABEL_MAP) {
    if (normalized.startsWith('ai')) {
      return { label: SOURCE_LABEL_MAP[normalized], key: 'ai_recommendations' };
    }
    if (normalized === 'custom') {
      return { label: SOURCE_LABEL_MAP[normalized], key: 'custom' };
    }
    if (normalized === 'website') {
      return { label: SOURCE_LABEL_MAP[normalized], key: 'website' };
    }
    return { label: SOURCE_LABEL_MAP[normalized], key: 'default' };
  }

  return { label: 'Default', key: 'default' };
};

export default function QuestionAnswer({ knowledgeBaseId }: any) {
  const theme = useTheme();
  const { locationId, agentId } = useParams();
  const [searchText, setSearchText] = useState<null | string>(null);
  const { currentPage, perPage, setPage, setPerPage } = usePagination();

  const { data: currentUser } = useGetCurrentUser();
  const { mutateAsync: importQaChunk } = useImportQaChunk();
  const { mutateAsync: deleteQaChunk } = useDeleteQaChunk();
  const { data: qaChunks, isLoading: isQaChunkLoading } = useGetQaChunks({
    page: currentPage,
    per_page: perPage,
    'filter[content]': searchText
  });

  const memoizedQaChunks = useMemo(() => qaChunks?.data ?? [], [qaChunks?.data]);

  const handleSearchTextChange = (value: string | null) => {
    if (searchText !== value) {
      setSearchText(value);
      setPage(1);
    }
  };

  const handleExport = async () => {
    const response = await http.getFile(`business/locations/${locationId}/agents/${agentId}/qa_chunks/export`);
    fileDownloader(response, 'agent-qa.csv');
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      await importQaChunk(formData);
    }
  };
  const triggerFileInput = () => {
    const inputElement = document.getElementById('import-qa-chunk') as HTMLInputElement;
    inputElement?.click();
  };

  return (
    <>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        mb={2}
        flexDirection={{ xs: 'column', lg: 'row' }}
        gap={{ xs: 1.5, lg: 0 }}
      >
        <Typography variant='h6' fontWeight={500} sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
          Q&A
        </Typography>
        <Box display='flex' gap={1} flexWrap='wrap' justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
          <Search
            size='small'
            sx={{
              background: theme.palette.background.paper,
              width: { xs: '100%', sm: 240, lg: 260 }
            }}
            placeholder='Search Question'
            searchText={searchText}
            setSearchText={handleSearchTextChange}
          />
          {['agency_admin', 'agency_admin_readonly', 'agent_builder'].includes(currentUser?.role ?? '') && (
            <Button
              sx={{
                background: theme.palette.background.paper,
                border: '1px solid #ECECEC',
                minWidth: { xs: 'auto', lg: 88 },
                px: { xs: 1.5, lg: 2 }
              }}
              color='secondary'
              variant='outlined'
              startIcon={<ExportIcon />}
              onClick={() => handleExport()}
            >
              Export
            </Button>
          )}
          <label htmlFor='import-qa-chunk'>
            <input
              type='file'
              name='import-qa-chunk'
              id='import-qa-chunk'
              style={{ display: 'none' }}
              accept='.csv'
              onChange={handleFileChange}
            />
            <Button
              sx={{
                background: theme.palette.background.paper,
                border: '1px solid #ECECEC',
                minWidth: { xs: 'auto', lg: 88 },
                px: { xs: 1.5, lg: 2 }
              }}
              color='secondary'
              variant='outlined'
              startIcon={<ImportIcon />}
              onClick={triggerFileInput}
            >
              Import
            </Button>
          </label>
          <Tooltip title='Download sample file' arrow placement='top'>
            <IconButton
              href='/sampleQa.csv'
              download={true}
              sx={{
                background: theme.palette.background.paper,
                border: '1px solid #ECECEC',
                p: { xs: 0.75, lg: 1 }
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <RetrievalTestingButton knowledgeBaseId={knowledgeBaseId} />
          <Link to='qa-chunk/add'>
            <Button
              color='primary'
              startIcon={<GoPlus />}
              sx={{
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Add New
            </Button>
          </Link>
        </Box>
      </Box>
      <DataTable
        data={memoizedQaChunks}
        columns={columns(deleteQaChunk)}
        isHeaderSticky={true}
        isLoading={isQaChunkLoading}
      />
      <Pagination
        totalPages={qaChunks?.meta?.total_pages}
        totalCount={qaChunks?.meta?.total_count ?? 0}
        currentPage={currentPage}
        perPage={perPage}
        onChange={setPage}
        onPerPageChange={setPerPage}
      />
    </>
  );
}

const columns = (deleteQaChunk: (id: string) => void): ColumnDef<QaChunkResponse>[] => [
  {
    header: 'Question',
    accessorKey: 'question',
    cell: (info) => <ReactMarkdown rehypePlugins={[rehypeRaw]}>{info.getValue() as string}</ReactMarkdown>
  },
  {
    header: 'Answer',
    accessorKey: 'answer',
    cell: (info) => <ReactMarkdown rehypePlugins={[rehypeRaw]}>{info.getValue() as string}</ReactMarkdown>
  },
  {
    header: 'Source',
    cell: (info) => {
      const rowData = info.row.original as QaChunkResponse;
      const { label, key } = getSourceMeta(rowData?.metadata?.source);

      return (
        <Box
          sx={{
            width: 'fit-content',
            px: 1.6,
            py: 0.35,
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.4,
            ...SOURCE_BADGE_SX[key]
          }}
        >
          {label}
        </Box>
      );
    }
  },
  {
    header: 'Action',
    cell: (info) => (
      <Box
        display='flex'
        gap={1}
        sx={{
          flexWrap: { xs: 'wrap', lg: 'nowrap' }
        }}
      >
        <Link to={`qa-chunk/${String(info.row.original.id)}/edit`}>
          <IconButton
            sx={{
              p: { xs: 0.5, lg: 1 }
            }}
          >
            <MdOutlineEdit />
          </IconButton>
        </Link>
        <IconButton
          color='error'
          onClick={() => deleteQaChunk(String(info.row.original.id))}
          sx={{
            p: { xs: 0.5, lg: 1 }
          }}
        >
          <RiDeleteBin6Line style={{ color: 'red' }} />
        </IconButton>
      </Box>
    )
  }
];
